import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { signOut, updateProfile } from 'firebase/auth';
import {
  collection, query, where, onSnapshot, doc, Timestamp, deleteField, getDocs, orderBy, limit,
  setDoc, getDoc, serverTimestamp, updateDoc, deleteDoc,  addDoc
} from 'firebase/firestore';
import { Users, UserPlus, LogOut, Search, MessageCircle, MoreVertical, MessageSquare, Trash2, Ban, X, UsersRound, Sun, Moon, Settings, Shield, Info, HeadphonesIcon, Crown, Palette, ChevronRight, Archive, ArchiveRestore, Pin, PinOff, QrCode, CalendarDays, Bell, Plus, CheckCircle2, LayoutGrid } from 'lucide-react';
import './board.css';
import type { ChatContact } from './types';
import { useTranslation } from '../../useTranslation';
import { clearSession } from '../../services/autoLogin';

interface Props {
  currentUser: any;
  chats: ChatContact[];
  activeChat: ChatContact | null;
  onSelectChat: (chat: ChatContact) => void;
  loading: boolean;
  SkeletonList?: React.ComponentType;
  onBoardOpen?: (open: boolean) => void;
}

export const Sidebar = ({ currentUser, chats, activeChat, onSelectChat, loading, SkeletonList, onBoardOpen }: Props & { className?: string }) => {
  const [chatCtxMenu, setChatCtxMenu] = useState<{ chat: ChatContact; x: number; y: number } | null>(null);
  const navigate = useNavigate();
  const { lang, setLang, t } = useTranslation();
  const [tab, setTab] = useState<'chats' | 'friends' | 'agenda' | 'board'>('chats');
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [msgSearchResults, setMsgSearchResults] = useState<any[]>([]);
  const [msgSearching, setMsgSearching] = useState(false);
  const searchMsgTimeout = useRef<any>(null);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [friendInput, setFriendInput] = useState('');
  const [friendError, setFriendError] = useState('');
  const [friendSuccess, setFriendSuccess] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [friends, setFriends] = useState<any[]>([]);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [searchResult, setSearchResult] = useState<any>(null);
  const [searching, setSearching] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [friendMenu, setFriendMenu] = useState<string | null>(null);
  // Group creation
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupError, setGroupError] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [groupPhoto, setGroupPhoto] = useState<string | null>(null);
  const [showBlocked, setShowBlocked] = useState(false);
  const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') !== 'light');
  const [userStatus, setUserStatus] = useState<string>(() => localStorage.getItem('userStatus') || 'online');
  const [customStatusText, setCustomStatusText] = useState<string>(() => localStorage.getItem('customStatusText') || '');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customInput, setCustomInput] = useState('');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsPage, setSettingsPage] = useState<string | null>(null);
  const [chatTheme, setChatTheme] = useState(() => localStorage.getItem('chatTheme') || 'default');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [agendaEvents, setAgendaEvents] = useState<any[]>([]);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [eventNote, setEventNote] = useState('');
  const [eventSaving, setEventSaving] = useState(false);
  const [eventReminderBefore, setEventReminderBefore] = useState<number>(30);
  const [eventInvitees, setEventInvitees] = useState<string[]>([]);
  const [eventInviteeSearch, setEventInviteeSearch] = useState('');
  const [notes, setNotes] = useState<any[]>([]);
  const [showNewNote, setShowNewNote] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingNote, setEditingNote] = useState<any>(null);
  const [agendaSubTab, setAgendaSubTab] = useState<'notes' | 'events'>('notes');
  const [noteReminderDate, setNoteReminderDate] = useState('');
  const [noteReminderTime, setNoteReminderTime] = useState('');
  const [showReminderPicker, setShowReminderPicker] = useState(false);
  const notifiedNotes = useRef<Set<string>>(new Set());
  const notifiedEvents = useRef<Set<string>>(new Set());
  const [showDailySummary, setShowDailySummary] = useState(false);
  const summaryShownRef = useRef(false);
  const [hideOnlineStatus, setHideOnlineStatus] = useState(false);
  const [hideLastSeen, setHideLastSeen] = useState(false);

  useEffect(() => {
    // Apply chat theme
    document.documentElement.setAttribute('data-chat-theme', chatTheme);
    // Apply light/dark mode
    const isLight = localStorage.getItem('theme') === 'light';
    document.body.classList.toggle('light-mode', isLight);
  }, []);

  // Load privacy settings from Firestore
  useEffect(() => {
    if (!currentUser) return;
    const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      const data = snap.data();
      if (data) {
        setHideOnlineStatus(data.hideOnlineStatus ?? false);
        setHideLastSeen(data.hideLastSeen ?? false);
      }
    });
    return () => unsub();
  }, [currentUser]);

  // Load agenda events
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'agenda'),
      where('uid', '==', currentUser.uid),
      orderBy('dateTime', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      setAgendaEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [currentUser]);

  // Load notes
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'notes'),
      where('uid', '==', currentUser.uid),
      orderBy('updatedAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setNotes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [currentUser]);

  // ── Daily summary popup ──
  useEffect(() => {
    if (summaryShownRef.current) return;
    if (!currentUser) return;
    // Wait until both notes and events are loaded
    const today = new Date().toDateString();
    const lastShown = localStorage.getItem('dailySummaryShown');
    if (lastShown === today) return;

    // Show after a short delay so app feels settled
    const t = setTimeout(() => {
      summaryShownRef.current = true;
      localStorage.setItem('dailySummaryShown', today);
      setShowDailySummary(true);
    }, 1500);
    return () => clearTimeout(t);
  }, [currentUser, agendaEvents, notes]);

  // ── Agenda notifications ──
  const agendaIntervalRef = useRef<any>(null);

  useEffect(() => {
    if (!currentUser) return;

    // Clear any previous interval before creating a new one
    if (agendaIntervalRef.current) {
      clearInterval(agendaIntervalRef.current);
      agendaIntervalRef.current = null;
    }

    const notificationIcon = userProfile?.photoURL || currentUser.photoURL || '/favicon.ico';

    const checkUpcoming = () => {
      const now = new Date();

      // ── Check note reminders ──
      notes.forEach(note => {
        if (!note.reminderAt) return;
        const dt = note.reminderAt?.toDate ? note.reminderAt.toDate() : new Date(note.reminderAt);
        const diffMs = dt.getTime() - now.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        const key = `note-${note.id}-reminder`;
        if (diffMin <= 0 && diffMin > -1 && !notifiedNotes.current.has(key)) {
          notifiedNotes.current.add(key);
          new Notification(`📝 ${note.title}`, {
            body: note.body ? note.body.slice(0, 80) : 'Tienes un recordatorio pendiente.',
            icon: notificationIcon,
            badge: notificationIcon,
            tag: key,
            requireInteraction: true,
          });
        }
      });

      agendaEvents.forEach(ev => {
        if (ev.done) return;
        const dt = ev.dateTime?.toDate ? ev.dateTime.toDate() : new Date(ev.dateTime);
        const diffMs = dt.getTime() - now.getTime();
        const diffMin = Math.floor(diffMs / 60000);

        // Use event's reminderBefore, fallback to [60,30,15,5]
        const alertWindows: number[] = ev.reminderBefore
          ? [ev.reminderBefore]
          : [60, 30, 15, 5];

        alertWindows.forEach(window => {
          const key = `${ev.id}-${window}`;
          if (diffMin <= window && diffMin > window - 1 && !notifiedEvents.current.has(key)) {
            notifiedEvents.current.add(key);
            const label = window === 60 ? '1 hora' : `${window} minutos`;
            new Notification(`📅 ${ev.title}`, {
              body: `En ${label} — ${dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
              icon: notificationIcon,
              badge: notificationIcon,
              tag: key,
            });
          }
        });

        // Notify exactly at the time
        const keyNow = `${ev.id}-now`;
        if (diffMin <= 0 && diffMin > -1 && !notifiedEvents.current.has(keyNow)) {
          notifiedEvents.current.add(keyNow);
          new Notification(`🔔 ¡Ahora! ${ev.title}`, {
            body: ev.note || 'Es la hora de tu evento.',
            icon: notificationIcon,
            badge: notificationIcon,
            tag: keyNow,
            requireInteraction: true,
          });
        }
      });
    };

    const start = async () => {
      if (Notification.permission === 'default') {
        await Notification.requestPermission();
      }
      if (Notification.permission !== 'granted') return;

      checkUpcoming();
      agendaIntervalRef.current = setInterval(checkUpcoming, 60000);
    };

    start();

    return () => {
      if (agendaIntervalRef.current) {
        clearInterval(agendaIntervalRef.current);
        agendaIntervalRef.current = null;
      }
    };
  }, [agendaEvents, notes, currentUser, userProfile]);

  const handleSaveNote = async () => {
    if (!noteTitle.trim()) return;
    setNoteSaving(true);
    try {
      const reminderAt = noteReminderDate && noteReminderTime
        ? new Date(`${noteReminderDate}T${noteReminderTime}`)
        : null;
      if (editingNote) {
        await updateDoc(doc(db, 'notes', editingNote.id), {
          title: noteTitle.trim(),
          body: noteBody.trim(),
          updatedAt: serverTimestamp(),
          ...(reminderAt && { reminderAt }),
        });
        setEditingNote(null);
      } else {
        await addDoc(collection(db, 'notes'), {
          uid: currentUser.uid,
          title: noteTitle.trim(),
          body: noteBody.trim(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...(reminderAt && { reminderAt }),
        });
      }
      setNoteTitle(''); setNoteBody('');
      setNoteReminderDate(''); setNoteReminderTime('');
      setShowReminderPicker(false); setShowNewNote(false);
    } catch(e) { console.error(e); }
    finally { setNoteSaving(false); }
  };

  const handleQuickReminder = (minutes: number) => {
    const dt = new Date(Date.now() + minutes * 60000);
    setNoteReminderDate(dt.toISOString().split('T')[0]);
    setNoteReminderTime(dt.toTimeString().slice(0, 5));
    setShowReminderPicker(false);
  };

  const handleDeleteNote = async (id: string) => {
    await deleteDoc(doc(db, 'notes', id));
  };

  const handleEditNote = (note: any) => {
    setEditingNote(note);
    setNoteTitle(note.title);
    setNoteBody(note.body || '');
    setShowNewNote(true);
  };

  const formatNoteDate = (dt: any) => {
    if (!dt) return '';
    const d = dt.toDate ? dt.toDate() : new Date(dt);
    return d.toLocaleDateString('es-DO', { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleSaveEvent = async () => {
    if (!eventTitle.trim() || !eventDate || !eventTime) return;
    setEventSaving(true);
    try {
      const dateTime = new Date(`${eventDate}T${eventTime}`);
      const eventData = {
        title: eventTitle.trim(),
        note: eventNote.trim(),
        dateTime,
        createdAt: serverTimestamp(),
        done: false,
        reminderBefore: eventReminderBefore,
        invitees: eventInvitees,
        createdBy: currentUser.uid,
      };

      // Save to creator's agenda
      await addDoc(collection(db, 'agenda'), {
        uid: currentUser.uid,
        ...eventData,
      });

      // Save to each invitee's agenda
      for (const inviteeUid of eventInvitees) {
        const inviterName = userProfile?.displayName || currentUser.displayName || 'Alguien';
        await addDoc(collection(db, 'agenda'), {
          uid: inviteeUid,
          ...eventData,
          note: `${eventData.note ? eventData.note + ' · ' : ''}Invitado por ${inviterName}`,
        });
      }

      setEventTitle(''); setEventDate(''); setEventTime(''); setEventNote('');
      setEventInvitees([]); setEventInviteeSearch('');
      setEventReminderBefore(30);
      setShowNewEvent(false);
    } catch (e) { console.error(e); }
    finally { setEventSaving(false); }
  };

  const handleDeleteEvent = async (id: string) => {
    await deleteDoc(doc(db, 'agenda', id));
  };

  const handleToggleDone = async (id: string, done: boolean) => {
    await updateDoc(doc(db, 'agenda', id), { done: !done });
  };

  const formatEventDate = (dt: any) => {
    if (!dt) return '';
    const d = dt.toDate ? dt.toDate() : new Date(dt);
    return d.toLocaleDateString('es-DO', { weekday: 'short', month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isToday = (dt: any) => {
    if (!dt) return false;
    const d = dt.toDate ? dt.toDate() : new Date(dt);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  };

  const isPast = (dt: any) => {
    if (!dt) return false;
    const d = dt.toDate ? dt.toDate() : new Date(dt);
    return d < new Date();
  };

  const handleSetStatus = async (status: string, text?: string) => {
    setUserStatus(status);
    localStorage.setItem('userStatus', status);
    const statusText = text !== undefined ? text : '';
    if (text !== undefined) {
      setCustomStatusText(text);
      localStorage.setItem('customStatusText', text);
    } else {
      setCustomStatusText('');
      localStorage.setItem('customStatusText', '');
    }
    setShowStatusMenu(false);
    setShowCustomInput(false);
    await updateDoc(doc(db, 'users', currentUser.uid), {
      status,
      statusText: statusText,
      online: status !== 'offline'
    });
  };

  const handleSaveCustomStatus = async () => {
    if (!customInput.trim()) return;
    await handleSetStatus('online', customInput.trim());
    setCustomInput('');
  };

  const statusConfig: Record<string, { label: string; color: string; emoji: string }> = {
    online:    { label: 'Disponible',   color: '#22c55e', emoji: '🟢' },
    busy:      { label: 'Ocupado',      color: '#f59e0b', emoji: '🟡' },
    dnd:       { label: 'No molestar',  color: '#ef4444', emoji: '🔴' },
    offline:   { label: 'Invisible',    color: '#71717a', emoji: '⚫' },
  };

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
    document.body.classList.toggle('light-mode', !next);
  };
  const [showProfile, setShowProfile] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [editName, setEditName] = useState('');

  // ── Mobile back button for modals ──
  useEffect(() => {
    const anyOpen = showSettings || showProfile || showQR || showAddFriend || showCreateGroup || showDailySummary || tab !== 'chats';
    if (anyOpen) {
      window.history.pushState({ level: 2 }, '');
    }
  }, [showSettings, showProfile, showQR, showAddFriend, showCreateGroup, showDailySummary, tab]);

  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      const state = e.state as any;
      // Only handle if we're at level 2 (sidebar modals/tabs)
      if (state?.level !== 2) return;
      if (showSettings) { setShowSettings(false); setSettingsPage(null); window.history.replaceState({ level: 0 }, ''); return; }
      if (showProfile) { setShowProfile(false); window.history.replaceState({ level: 0 }, ''); return; }
      if (showQR) { setShowQR(false); window.history.replaceState({ level: 0 }, ''); return; }
      if (showAddFriend) { setShowAddFriend(false); window.history.replaceState({ level: 0 }, ''); return; }
      if (showCreateGroup) { setShowCreateGroup(false); window.history.replaceState({ level: 0 }, ''); return; }
      if (showDailySummary) { setShowDailySummary(false); window.history.replaceState({ level: 0 }, ''); return; }
      if (tab !== 'chats') { setTab('chats'); window.history.replaceState({ level: 0 }, ''); return; }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showSettings, showProfile, showQR, showAddFriend, showCreateGroup, showDailySummary, tab]);
  const [editBio, setEditBio] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const searchTimeout = useRef<any>(null);

  // Load user profile
  useEffect(() => {
    if (!currentUser) return;
    const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (snap) => {
      setUserProfile(snap.data());
    });
    return () => unsub();
  }, [currentUser]);

  // Load friends + pending count
  useEffect(() => {
    if (!currentUser) return;
    let incoming: any[] = [];
    let outgoing: any[] = [];

    const unsub1 = onSnapshot(
      query(collection(db, 'friends'), where('toUid', '==', currentUser.uid)),
      (snap) => {
        incoming = snap.docs.map(d => ({ id: d.id, ...d.data(), direction: 'incoming' }));
        setPendingCount(incoming.filter((f: any) => f.status === 'pending').length);
        setFriends([...incoming, ...outgoing]);
      }
    );

    const unsub2 = onSnapshot(
      query(collection(db, 'friends'), where('fromUid', '==', currentUser.uid)),
      (snap) => {
        outgoing = snap.docs.map(d => ({ id: d.id, ...d.data(), direction: 'outgoing' }));
        setFriends([...incoming, ...outgoing]);
      }
    );

    return () => { unsub1(); unsub2(); };
  }, [currentUser]);

  // Real-time search
  const handleSearchInput = (val: string) => {
    setFriendInput(val);
    setFriendError('');
    setFriendSuccess('');
    setSearchResult(null);
    clearTimeout(searchTimeout.current);
    if (val.trim().length < 2) return;

    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const clean = val.toLowerCase().replace('@', '');
        const usernameSnap = await getDoc(doc(db, 'usernames', clean));
        if (usernameSnap.exists()) {
          const uid = usernameSnap.data().uid;
          const userSnap = await getDoc(doc(db, 'users', uid));
          setSearchResult({ uid, ...userSnap.data() });
        } else {
          const q = query(collection(db, 'users'), where('email', '==', val.toLowerCase()));
          const snap = await getDocs(q);
          if (!snap.empty) {
            setSearchResult({ uid: snap.docs[0].id, ...snap.docs[0].data() });
          } else {
            setSearchResult(null);
          }
        }
      } catch { setSearchResult(null); }
      finally { setSearching(false); }
    }, 400);
  };

  const handleSendRequest = async () => {
    if (!searchResult) return;
    setFriendError('');
    setFriendSuccess('');

    if (searchResult.uid === currentUser.uid) {
      setFriendError('No puedes agregarte a ti mismo. 😅');
      return;
    }

    const friendRef = doc(db, 'friends', `${currentUser.uid}_${searchResult.uid}`);
    const reverseRef = doc(db, 'friends', `${searchResult.uid}_${currentUser.uid}`);
    const [existing, reverse] = await Promise.all([getDoc(friendRef), getDoc(reverseRef)]);

    if (existing.exists() || reverse.exists()) {
      setFriendError('Ya son amigos o hay una solicitud pendiente.');
      return;
    }

    setAddLoading(true);
    try {
      await setDoc(friendRef, {
        fromUid: currentUser.uid,
        toUid: searchResult.uid,
        fromUsername: userProfile?.username || currentUser.displayName,
        fromDisplayName: userProfile?.displayName || currentUser.displayName,
        toUsername: searchResult.username,
        toDisplayName: searchResult.displayName,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setFriendSuccess('¡Solicitud enviada! 🎉');
      setSearchResult(null);
      setFriendInput('');
    } catch {
      setFriendError('Error al enviar solicitud.');
    } finally {
      setAddLoading(false);
    }
  };

  const handleAcceptFriend = async (friend: any) => {
    await updateDoc(doc(db, 'friends', friend.id), { status: 'accepted' });
  };

  const handleDeleteFriend = async (friend: any) => {
    await deleteDoc(doc(db, 'friends', friend.id));
    setFriendMenu(null);
  };

  const handleBlockFriend = async (friend: any) => {
    await updateDoc(doc(db, 'friends', friend.id), { status: 'blocked', blockedBy: currentUser.uid });
    setFriendMenu(null);
  };

  const handleUnblock = async (friend: any) => {
    await updateDoc(doc(db, 'friends', friend.id), { status: 'accepted', blockedBy: null });
    setFriendMenu(null);
  };

  const handleStartChat = async (friend: any) => {
    const otherUid = friend.fromUid === currentUser.uid ? friend.toUid : friend.fromUid;
    const chatId = [currentUser.uid, otherUid].sort().join('_');
    const chatRef = doc(db, 'chats', chatId);
    try {
      const existing = await getDoc(chatRef);
      console.log('chat exists:', existing.exists());
      if (!existing.exists()) {
        await setDoc(chatRef, {
          members: [currentUser.uid, otherUid],
          lastMessage: '',
          lastMessageTime: Timestamp.now(),
          unreadCount: { [currentUser.uid]: 0, [otherUid]: 0 },
          createdAt: Timestamp.now(),
          isGroup: false,
        });
      } else {
        // Chat exists but may have been deleted — clear it
        const chatData = existing.data();
        const updates: any = {};
        if (chatData?.deletedBy?.[currentUser.uid]) {
          updates[`deletedBy.${currentUser.uid}`] = deleteField();
        }
        if (chatData?.deletedAt?.[currentUser.uid]) {
          updates[`deletedAt.${currentUser.uid}`] = deleteField();
        }
        if (Object.keys(updates).length > 0) {
          await updateDoc(chatRef, updates);
        }
      }
    } catch(e) { console.error('chat create error:', e); }
    const otherProfile = await getDoc(doc(db, 'users', otherUid));
    const data = otherProfile.data();
    onSelectChat({
      chatId,
      uid: otherUid,
      displayName: data?.displayName || data?.username || 'Usuario',
      username: data?.username || '',
      photoURL: data?.photoURL,
      online: data?.online || false,
      lastMessage: '',
      lastMessageTime: null,
      unreadCount: 0,
      isGroup: false,
    });
    setTab('chats');
  };

  // ── Create Group ──
  const toggleMember = (uid: string) => {
    setSelectedMembers(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) { setGroupError('Escribe un nombre para el grupo.'); return; }
    if (selectedMembers.length < 1) { setGroupError('Selecciona al menos un miembro.'); return; }

    setGroupLoading(true);
    setGroupError('');
    try {
      const members = [currentUser.uid, ...selectedMembers];
      const unreadCount: any = {};
      members.forEach(uid => { unreadCount[uid] = 0; });

      // Get member profiles for display
      const memberProfiles: any = {};
      for (const uid of members) {
        const snap = await getDoc(doc(db, 'users', uid));
        if (snap.exists()) {
          const d = snap.data();
          memberProfiles[uid] = { displayName: d.displayName || d.username || 'Usuario', photoURL: d.photoURL || null };
        }
      }

      const chatRef = await addDoc(collection(db, 'chats'), {
        isGroup: true,
        groupName: groupName.trim(),
        groupPhoto: groupPhoto || null,
        description: groupDescription.trim(),
        admins: [currentUser.uid],
        members,
        memberProfiles,
        lastMessage: '',
        lastMessageTime: serverTimestamp(),
        unreadCount,
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
      });

      onSelectChat({
        chatId: chatRef.id,
        uid: '',
        displayName: groupName.trim(),
        username: '',
        online: false,
        lastMessage: '',
        lastMessageTime: null,
        unreadCount: 0,
        isGroup: true,
        members,
        memberProfiles,
      });

      setShowCreateGroup(false);
      setGroupName('');
      setGroupDescription('');
      setGroupPhoto(null);
      setSelectedMembers([]);
      setTab('chats');
    } catch (e) {
      setGroupError('Error al crear el grupo.');
      console.error(e);
    } finally {
      setGroupLoading(false);
    }
  };

  const handleSaveProfile = async (photoData?: string) => {
    setProfileSaving(true);
    setProfileError('');
    try {
      const updates: any = {};
      if (editName.trim()) updates.displayName = editName.trim();
      if (editBio.trim() !== undefined) updates.bio = editBio;
      if (photoData) updates.photoURL = photoData;
      await updateDoc(doc(db, 'users', currentUser.uid), { ...updates });
      // Only update displayName in Auth — photoURL stays in Firestore only (base64 not valid for Auth)
      await updateProfile(currentUser, {
        displayName: editName.trim() || currentUser.displayName,
      });
      setShowProfile(false);
    } catch (e) {
      setProfileError('Error al guardar cambios.');
    } finally {
      setProfileSaving(false);
    }
  };

  const resizeImage = (file: File, maxSize: number): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('canvas error')), 'image/jpeg', 0.85);
      };
      img.onerror = reject;
      img.src = url;
    });

  const handlePhotoUpload = async (file: File) => {
    setProfileSaving(true);
    setProfileError('');
    try {
      const resized = await resizeImage(file, 400);
      const storageRef = ref(storage, `avatars/${currentUser.uid}`);
      await uploadBytes(storageRef, resized);
      const photoURL = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'users', currentUser.uid), { photoURL });
      await updateProfile(currentUser, { photoURL });
    } catch (e) {
      setProfileError('Error al subir la foto.');
      console.error(e);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm('⚠️ ¿Eliminar tu cuenta permanentemente? Esta acción no se puede deshacer.');
    if (!confirmed) return;
    const confirmed2 = window.prompt('Escribe DELETE para confirmar:');
    if (confirmed2 !== 'DELETE') return;
    setDeletingAccount(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { deleted: true, displayName: 'Usuario eliminado', photoURL: null });
      await currentUser.delete();
      navigate('/');
    } catch (e: any) {
      if (e.code === 'auth/requires-recent-login') alert('Por seguridad, cierra sesión y vuelve a iniciarla antes de eliminar tu cuenta.');
      else alert('Error al eliminar la cuenta.');
    } finally { setDeletingAccount(false); }
  };

  const handleChatTheme = (theme: string) => {
    setChatTheme(theme);
    localStorage.setItem('chatTheme', theme);
    document.documentElement.setAttribute('data-chat-theme', theme);
  };

  const handleLogout = async () => {
    const confirmed = window.confirm('¿Seguro que quieres cerrar sesión?');
    if (!confirmed) return;
    try {
      await setDoc(doc(db, 'users', currentUser.uid), { online: false, lastSeen: serverTimestamp() }, { merge: true });
    } catch (_) {}
    clearSession();
    await signOut(auth);
    navigate('/');
  };

  const handleSearchChange = (val: string) => {
    setSearch(val);
    clearTimeout(searchMsgTimeout.current);
    if (!val.trim()) { setMsgSearchResults([]); return; }
    setMsgSearching(true);
    searchMsgTimeout.current = setTimeout(async () => {
      try {
        const results: any[] = [];
        for (const chat of chats) {
          const q = query(
            collection(db, 'chats', chat.chatId, 'messages'),
            where('type', '==', 'text'),
            orderBy('createdAt', 'desc'),
            limit(50)
          );
          const snap = await getDocs(q);
          snap.docs.forEach(d => {
            const msg = d.data();
            if (msg.text?.toLowerCase().includes(val.toLowerCase())) {
              results.push({ ...msg, id: d.id, chatId: chat.chatId, chatName: chat.displayName, chatPhoto: chat.photoURL, isGroup: chat.isGroup });
            }
          });
        }
        setMsgSearchResults(results.slice(0, 20));
      } catch (e) { console.error(e); }
      finally { setMsgSearching(false); }
    }, 500);
  };

  const filteredChats = chats.filter(c => {
    if (c.deletedBy?.[currentUser.uid]) {
      // Show again only if a new message arrived after deletion
      const deletedAt = (c as any).deletedAt?.[currentUser.uid];
      if (!deletedAt || !c.lastMessageTime) return false;
      const lastMsgTime = c.lastMessageTime?.toMillis ? c.lastMessageTime.toMillis() : new Date(c.lastMessageTime).getTime();
      if (lastMsgTime <= deletedAt) return false;
    }
    return c.displayName.toLowerCase().includes(search.toLowerCase()) ||
      (c.username || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.lastMessage || '').toLowerCase().includes(search.toLowerCase());
  });

  const formatTime = (ts: any) => {
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getInitials = (name: string) => (name || 'U').slice(0, 2).toUpperCase();
  const activeFriends = friends.filter((f: any) => f.status === 'accepted');
  const pendingFriends = friends.filter((f: any) => f.status === 'pending');
  const blockedFriends = friends.filter((f: any) => f.status === 'blocked' && f.blockedBy === currentUser.uid);

  return (
    <>
      <aside className="sidebar" onClick={() => setFriendMenu(null)}>
        {/* Profile */}
        <div className="sidebar__profile">
          <div style={{ position: 'relative' }}>
            <div className="sidebar__avatar sidebar__avatar--clickable" onClick={() => { setShowProfile(true); setEditName(userProfile?.displayName || currentUser.displayName || ''); setEditBio(userProfile?.bio || ''); setProfileError(''); }} title="Editar perfil">
              {(userProfile?.photoURL || currentUser.photoURL)
                ? <img src={userProfile?.photoURL || currentUser.photoURL} alt="" />
                : getInitials(userProfile?.displayName || currentUser.displayName || 'U')}
              <div className="sidebar__avatar-edit">✏️</div>
            </div>
            <div className="sidebar__status-dot" style={{ background: statusConfig[userStatus]?.color || '#22c55e' }}
              onClick={() => setShowStatusMenu(s => !s)} title="Cambiar estado" />
            {showStatusMenu && (
              <div className="status-menu">
                {customStatusText && (
                  <div style={{ padding: '0.4rem 0.75rem', fontSize: '0.78rem', color: 'var(--text-dim)', borderBottom: '1px solid var(--glass-border)', marginBottom: '0.25rem' }}>
                    <span style={{ color: '#22c55e' }}>●</span> {customStatusText}
                    <button onClick={() => handleSetStatus('online')} style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '0.72rem' }}>✕ quitar</button>
                  </div>
                )}
                {Object.entries(statusConfig).map(([key, val]) => (
                  <button key={key} className="status-menu__item" onClick={() => handleSetStatus(key)}>
                    <span>{val.emoji}</span>
                    <span>{val.label}</span>
                    {userStatus === key && <span style={{ marginLeft: 'auto', color: 'var(--primary)' }}>✓</span>}
                  </button>
                ))}
                <div style={{ borderTop: '1px solid var(--glass-border)', marginTop: '0.25rem', padding: '0.35rem 0.25rem 0' }}>
                  {!showCustomInput ? (
                    <button className="status-menu__item" onClick={() => { setShowCustomInput(true); setCustomInput(customStatusText); }}>
                      ✏️ Estado personalizado
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.35rem', padding: '0.25rem' }}>
                      <input
                        autoFocus
                        value={customInput}
                        onChange={e => setCustomInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveCustomStatus(); if (e.key === 'Escape') setShowCustomInput(false); }}
                        placeholder="Ej: En reunión..."
                        maxLength={40}
                        style={{ flex: 1, background: 'var(--input-bg)', border: '1px solid var(--glass-border)', borderRadius: 8, padding: '0.3rem 0.5rem', color: 'var(--text-main)', fontSize: '0.8rem', outline: 'none', fontFamily: 'DM Sans, sans-serif' }}
                      />
                      <button onClick={handleSaveCustomStatus}
                        style={{ background: 'var(--primary)', border: 'none', borderRadius: 8, padding: '0 0.5rem', color: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}>✓</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="sidebar__info">
            <div className="sidebar__name">{userProfile?.displayName || currentUser.displayName || 'Usuario'}</div>
            <div className="sidebar__username">@{userProfile?.username || ''}</div>
            {customStatusText && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '0.1rem' }}>● {customStatusText}</div>
            )}
          </div>
          <div className="sidebar__actions">
            <button className="sidebar__icon-btn" title="Nuevo grupo" onClick={() => { setShowCreateGroup(true); setGroupName(''); setSelectedMembers([]); setGroupError(''); }}>
              <UsersRound size={18} />
            </button>
            <button className="sidebar__icon-btn" title="Agregar amigo" onClick={() => { setShowAddFriend(true); setFriendInput(''); setSearchResult(null); setFriendError(''); setFriendSuccess(''); }}>
              <UserPlus size={18} />
            </button>
            <button className="sidebar__icon-btn" title="Configuraciones" onClick={() => { setShowSettings(true); setSettingsPage(null); }}>
              <Settings size={18} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="sidebar__tabs">
          <button className={`sidebar__tab ${tab === 'chats' ? 'sidebar__tab--active' : ''}`} onClick={() => { setTab('chats'); onBoardOpen?.(false); }}>
            <MessageCircle size={16} /> Chats
          </button>
          <button className={`sidebar__tab ${tab === 'friends' ? 'sidebar__tab--active' : ''}`} onClick={() => { setTab('friends'); onBoardOpen?.(false); }}>
            <Users size={16} /> Amigos
            {pendingCount > 0 && <span className="unread-badge" style={{ marginLeft: '0.3rem' }}>{pendingCount}</span>}
          </button>
          <button className={`sidebar__tab ${tab === 'agenda' ? 'sidebar__tab--active' : ''}`} onClick={() => { setTab('agenda'); onBoardOpen?.(false); }}>
            <CalendarDays size={16} /> Agenda
            {agendaEvents.filter(e => isToday(e.dateTime) && !e.done).length > 0 && (
              <span className="unread-badge" style={{ marginLeft: '0.3rem' }}>{agendaEvents.filter(e => isToday(e.dateTime) && !e.done).length}</span>
            )}
          </button>
          <button className={`sidebar__tab ${tab === 'board' ? 'sidebar__tab--active' : ''}`} onClick={() => { setTab('board'); onBoardOpen?.(true); }}>
            <LayoutGrid size={16} /> Board
          </button>
        </div>

        {/* Search */}
        {tab !== 'agenda' && <div className="sidebar__search">
          <div className="sidebar__search-wrap">
            <Search size={15} />
            <input
              className="sidebar__search-input"
              placeholder={t.chat.searchPlaceholder}
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
            />
            {search && (
              <button onClick={() => { setSearch(''); setMsgSearchResults([]); }} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', padding: '0 0.25rem', display: 'flex' }}>
                <X size={14} />
              </button>
            )}
          </div>
        </div>}

        {/* Chats list */}
        {tab === 'chats' && (
          <div className="sidebar__list">
            {loading && SkeletonList && <SkeletonList />}
            {loading && !SkeletonList && <div className="sidebar__empty">Cargando...</div>}
            {!loading && filteredChats.length === 0 && (
              <div className="sidebar__empty">
                <p>No tienes chats aún.</p>
                <p>Agrega amigos para comenzar.</p>
              </div>
            )}
            {search && msgSearchResults.length > 0 && (
              <div style={{ borderBottom: '1px solid var(--glass-border)', paddingBottom: '0.5rem', marginBottom: '0.5rem' }}>
                <div style={{ padding: '0.4rem 1rem', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Mensajes {msgSearching ? '...' : `(${msgSearchResults.length})`}
                </div>
                {msgSearchResults.map((r: any) => {
                  const chat = chats.find(c => c.chatId === r.chatId);
                  return (
                    <div key={r.id} className="sidebar__list-item" onClick={() => chat && onSelectChat(chat)}
                      style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.2rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
                        <div className="sidebar__item-avatar" style={{ width: 28, height: 28, fontSize: '0.7rem', flexShrink: 0 }}>
                          {r.chatPhoto ? <img src={r.chatPhoto} alt="" /> : getInitials(r.chatName)}
                        </div>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-main)' }}>{r.chatName}</span>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginLeft: 'auto' }}>
                          {r.senderName === currentUser.displayName ? 'Tú' : r.senderName}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', paddingLeft: '2rem', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.text?.replace(new RegExp(`(${search})`, 'gi'), '**$1**')}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {search && filteredChats.length > 0 && (
              <div style={{ padding: '0.4rem 1rem', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Chats
              </div>
            )}
            {(() => {
              const pinnedChats = filteredChats.filter((c: any) => (c as any).pinnedBy?.[currentUser.uid] && !c.archivedBy?.[currentUser.uid]);
              const activeChats = filteredChats.filter((c: any) => !(c as any).pinnedBy?.[currentUser.uid] && !c.archivedBy?.[currentUser.uid]);
              const archivedChats = filteredChats.filter((c: any) => c.archivedBy?.[currentUser.uid]);
              const renderChat = (chat: ChatContact) => (
                <div
                  key={chat.chatId}
                  className={`sidebar__list-item ${activeChat?.chatId === chat.chatId ? 'sidebar__list-item--active' : ''}`}
                  onClick={() => onSelectChat(chat)}
                  onContextMenu={e => { e.preventDefault(); const isPinned = !!(chat as any).pinnedBy?.[currentUser.uid]; const isArchived = !!(chat as any).archivedBy?.[currentUser.uid]; setChatCtxMenu({ chat: {...chat, pinnedBy: isPinned as any, archivedBy: isArchived as any}, x: e.clientX, y: e.clientY }); }}
                >
                  <div className="sidebar__item-avatar">
                    {chat.isGroup ? (
                      <span style={{ fontSize: '1.1rem' }}>👥</span>
                    ) : chat.photoURL ? (
                      <img src={chat.photoURL} alt="" />
                    ) : (
                      getInitials(chat.displayName)
                    )}
                    {!chat.isGroup && chat.online && <span className="online-dot" />}
                  </div>
                  <div className="sidebar__item-body">
                    <div className="sidebar__item-top">
                      <span className="sidebar__item-name">{chat.displayName}</span>
                      <span className="sidebar__item-time">{formatTime(chat.lastMessageTime)}</span>
                    </div>
                    <div className="sidebar__item-bottom">
                      <span className="sidebar__item-preview">{chat.lastMessage || 'Sin mensajes aún'}</span>
                      {(chat.unreadCount ?? 0) > 0 && <span className="unread-badge">{chat.unreadCount}</span>}
                    </div>
                  </div>
                </div>
              );
              return (
                <>
                  {pinnedChats.length > 0 && (
                    <>
                      <div style={{ padding: '0.3rem 1rem', fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        <Pin size={11} /> Fijados
                      </div>
                      {pinnedChats.map(chat => (
                        <div key={chat.chatId} style={{ position: 'relative' }}>
                          {renderChat(chat)}
                          <Pin size={11} style={{ position: 'absolute', top: 8, right: 8, color: 'var(--primary)', opacity: 0.7 }} />
                        </div>
                      ))}
                      {activeChats.length > 0 && <div style={{ height: 1, background: 'var(--glass-border)', margin: '0.2rem 0' }} />}
                    </>
                  )}
                  {activeChats.map(renderChat)}
                  {archivedChats.length > 0 && (
                    <>
                      <div className="archived-header" onClick={() => setShowArchived(p => !p)}>
                        <Archive size={14} />
                        <span>Archivados ({archivedChats.length})</span>
                        <span style={{ marginLeft: 'auto', fontSize: '0.7rem' }}>{showArchived ? '▲' : '▼'}</span>
                      </div>
                      {showArchived && archivedChats.map(renderChat)}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Friends list */}
        {tab === 'friends' && (
          <div className="friends-list">
            {pendingFriends.length > 0 && (
              <>
                <div className="friends-section-label">Solicitudes pendientes</div>
                {pendingFriends.map((friend: any) => {
                  const isIncoming = friend.direction === 'incoming';
                  const name = isIncoming ? (friend.fromDisplayName || friend.fromUsername) : (friend.toDisplayName || friend.toUsername);
                  const username = isIncoming ? friend.fromUsername : friend.toUsername;
                  return (
                    <div key={friend.id} className="friend-item">
                      <div className="sidebar__item-avatar">{getInitials(name)}</div>
                      <div className="friend-item__info">
                        <div className="friend-item__name">{name || 'Usuario'}</div>
                        <div className="friend-item__username">@{username} · {isIncoming ? 'Te envió solicitud' : 'Enviada'}</div>
                      </div>
                      <div className="friend-item__actions">
                        {isIncoming && (
                          <button className="friend-item__btn friend-item__btn--accept" onClick={() => handleAcceptFriend(friend)}>Aceptar</button>
                        )}
                        <button className="friend-item__btn" onClick={() => handleDeleteFriend(friend)}>✕</button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {activeFriends.length > 0 && (
              <>
                <div className="friends-section-label">Amigos — {activeFriends.length}</div>
                {activeFriends.map((friend: any) => {
                  const name = friend.fromUid === currentUser.uid
                    ? (friend.toDisplayName || friend.toUsername)
                    : (friend.fromDisplayName || friend.fromUsername);
                  const username = friend.fromUid === currentUser.uid ? friend.toUsername : friend.fromUsername;
                  return (
                    <div key={friend.id} className="friend-item" style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                      <div className="sidebar__item-avatar">{getInitials(name)}</div>
                      <div className="friend-item__info">
                        <div className="friend-item__name">{name || 'Usuario'}</div>
                        <div className="friend-item__username">@{username}</div>
                      </div>
                      <div className="friend-item__actions">
                        <button className="friend-item__btn friend-item__btn--chat" onClick={() => handleStartChat(friend)}>
                          <MessageSquare size={14} />
                        </button>
                        <button className="sidebar__icon-btn" style={{ padding: '0.3rem' }} onClick={() => setFriendMenu(friendMenu === friend.id ? null : friend.id)}>
                          <MoreVertical size={15} />
                        </button>
                      </div>
                      {friendMenu === friend.id && (
                        <div className="friend-menu">
                          <button className="friend-menu__item friend-menu__item--danger" onClick={() => handleDeleteFriend(friend)}>
                            <Trash2 size={14} /> Eliminar amigo
                          </button>
                          <button className="friend-menu__item friend-menu__item--danger" onClick={() => handleBlockFriend(friend)}>
                            <Ban size={14} /> Bloquear
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {blockedFriends.length > 0 && (
              <>
                <div className="friends-section-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '1.25rem' }}>
                  <span>Bloqueados — {blockedFriends.length}</span>
                  <button style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '0.72rem' }}
                    onClick={() => setShowBlocked(b => !b)}>{showBlocked ? 'Ocultar' : 'Mostrar'}</button>
                </div>
                {showBlocked && blockedFriends.map((friend: any) => {
                  const name = friend.fromUid === currentUser.uid
                    ? (friend.toDisplayName || friend.toUsername)
                    : (friend.fromDisplayName || friend.fromUsername);
                  const username = friend.fromUid === currentUser.uid ? friend.toUsername : friend.fromUsername;
                  return (
                    <div key={friend.id} className="friend-item" style={{ position: 'relative', opacity: 0.6 }} onClick={e => e.stopPropagation()}>
                      <div className="sidebar__item-avatar" style={{ filter: 'grayscale(1)' }}>{getInitials(name)}</div>
                      <div className="friend-item__info">
                        <div className="friend-item__name">{name || 'Usuario'}</div>
                        <div className="friend-item__username">@{username} · Bloqueado</div>
                      </div>
                      <button className="friend-item__btn friend-item__btn--accept" style={{ fontSize: '0.72rem', padding: '0.25rem 0.5rem' }}
                        onClick={() => handleUnblock(friend)}>Desbloquear</button>
                    </div>
                  );
                })}
              </>
            )}

            {activeFriends.length === 0 && pendingFriends.length === 0 && blockedFriends.length === 0 && (
              <div className="sidebar__empty">
                <p>No tienes amigos aún.</p>
                <p>¡Agrega a alguien! 👆</p>
              </div>
            )}
          </div>
        )}
        {/* Agenda */}
        {tab === 'agenda' && (
          <div className="agenda-wrap">

            {/* Sub-tabs */}
            <div className="agenda-subtabs">
              <button className={`agenda-subtab ${agendaSubTab === 'notes' ? 'agenda-subtab--active' : ''}`} onClick={() => setAgendaSubTab('notes')}>
                📝 Notas {notes.length > 0 && <span className="agenda-subtab__count">{notes.length}</span>}
              </button>
              <button className={`agenda-subtab ${agendaSubTab === 'events' ? 'agenda-subtab--active' : ''}`} onClick={() => setAgendaSubTab('events')}>
                📅 Eventos {agendaEvents.filter(e => !e.done).length > 0 && <span className="agenda-subtab__count">{agendaEvents.filter(e => !e.done).length}</span>}
              </button>
              <button className="agenda-add-btn" onClick={() => { setShowNewNote(agendaSubTab === 'notes'); setShowNewEvent(agendaSubTab === 'events'); setEditingNote(null); setNoteTitle(''); setNoteBody(''); }}>
                <Plus size={15} />
              </button>
            </div>

            {/* ── NOTES ── */}
            {agendaSubTab === 'notes' && (
              <>
                {(showNewNote) && (
                  <div className="agenda-form">
                    <input
                      className="agenda-input"
                      placeholder="Título de la nota..."
                      value={noteTitle}
                      onChange={e => setNoteTitle(e.target.value)}
                      autoFocus
                    />
                    <textarea
                      className="agenda-input"
                      placeholder="Escribe aquí..."
                      value={noteBody}
                      onChange={e => setNoteBody(e.target.value)}
                      rows={5}
                      style={{ resize: 'none' }}
                    />
                    {/* Reminder section */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                        {[
                          { label: '30 min', min: 30 },
                          { label: '1 hora', min: 60 },
                          { label: '3 horas', min: 180 },
                          { label: 'Mañana 9am', min: null },
                        ].map(opt => (
                          <button key={opt.label} className="note-reminder-chip"
                            onClick={() => opt.min ? handleQuickReminder(opt.min) : handleQuickReminder(
                              (() => { const t = new Date(); t.setDate(t.getDate()+1); t.setHours(9,0,0,0); return Math.floor((t.getTime()-Date.now())/60000); })()
                            )}>
                            ⏰ {opt.label}
                          </button>
                        ))}
                        <button className="note-reminder-chip" onClick={() => setShowReminderPicker(p => !p)}>
                          📅 Fecha
                        </button>
                      </div>
                      {showReminderPicker && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <input type="date" className="agenda-input" value={noteReminderDate} onChange={e => setNoteReminderDate(e.target.value)} style={{ flex: 1 }} />
                          <input type="time" className="agenda-input" value={noteReminderTime} onChange={e => setNoteReminderTime(e.target.value)} style={{ flex: 1 }} />
                        </div>
                      )}
                      {noteReminderDate && noteReminderTime && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.3rem 0.6rem', background: 'color-mix(in srgb, var(--primary) 12%, transparent)', borderRadius: 8 }}>
                          <span style={{ fontSize: '0.78rem', color: 'var(--primary)' }}>
                            🔔 {new Date(`${noteReminderDate}T${noteReminderTime}`).toLocaleDateString('es-DO', { weekday: 'short', month: 'short', day: 'numeric' })} · {noteReminderTime}
                          </span>
                          <button onClick={() => { setNoteReminderDate(''); setNoteReminderTime(''); setShowReminderPicker(false); }}
                            style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.75rem' }}>✕</button>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="agenda-save-btn" onClick={handleSaveNote} disabled={noteSaving || !noteTitle.trim()}>
                        {noteSaving ? '...' : editingNote ? '✓ Actualizar' : '✓ Guardar'}
                      </button>
                      <button className="agenda-cancel-btn" onClick={() => { setShowNewNote(false); setEditingNote(null); setNoteTitle(''); setNoteBody(''); setNoteReminderDate(''); setNoteReminderTime(''); setShowReminderPicker(false); }}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {notes.length === 0 && !showNewNote && (
                  <div className="sidebar__empty">
                    <p>Sin notas aún.</p>
                    <p>Toca + para crear una 📝</p>
                  </div>
                )}

                <div className="agenda-list">
                  {notes.map(note => (
                    <div key={note.id} className="note-card" onClick={() => handleEditNote(note)}>
                      <div className="note-card__top">
                        <div className="note-card__title">{note.title}</div>
                        <button className="agenda-event__del" onClick={e => { e.stopPropagation(); handleDeleteNote(note.id); }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                      {note.body && <div className="note-card__body">{note.body}</div>}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div className="note-card__date">{formatNoteDate(note.updatedAt)}</div>
                        {note.reminderAt && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                            🔔 {formatNoteDate(note.reminderAt)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── EVENTS ── */}
            {agendaSubTab === 'events' && (
              <>
                {showNewEvent && (
                  <div className="agenda-form">
                    <input className="agenda-input" placeholder="Título del evento..." value={eventTitle} onChange={e => setEventTitle(e.target.value)} autoFocus />
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input type="date" className="agenda-input" value={eventDate} onChange={e => setEventDate(e.target.value)} style={{ flex: 1 }} />
                      <input type="time" className="agenda-input" value={eventTime} onChange={e => setEventTime(e.target.value)} style={{ flex: 1 }} />
                    </div>
                    <textarea className="agenda-input" placeholder="Nota (opcional)..." value={eventNote} onChange={e => setEventNote(e.target.value)} rows={2} style={{ resize: 'none' }} />

                    {/* Reminder before */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                      <Bell size={13} color="var(--primary)" />
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Notificar</span>
                      <select
                        className="agenda-input"
                        style={{ flex: 1, padding: '0.3rem 0.5rem', fontSize: '0.82rem' }}
                        value={eventReminderBefore}
                        onChange={e => setEventReminderBefore(Number(e.target.value))}
                      >
                        <option value={5}>5 min antes</option>
                        <option value={15}>15 min antes</option>
                        <option value={30}>30 min antes</option>
                        <option value={60}>1 hora antes</option>
                        <option value={120}>2 horas antes</option>
                        <option value={1440}>1 día antes</option>
                      </select>
                    </div>

                    {/* Invitees */}
                    <div style={{ marginBottom: '0.4rem' }}>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Users size={13} color="var(--primary)" /> Invitar contactos
                      </div>
                      <input
                        className="agenda-input"
                        placeholder="Buscar amigo..."
                        value={eventInviteeSearch}
                        onChange={e => setEventInviteeSearch(e.target.value)}
                        style={{ marginBottom: '0.3rem' }}
                      />
                      {/* Friend suggestions */}
                      {eventInviteeSearch.trim() && (() => {
                        const filtered = activeFriends.filter((f: any) => {
                          const name = f.fromUid === currentUser.uid ? f.toDisplayName : f.fromDisplayName;
                          const uname = f.fromUid === currentUser.uid ? f.toUsername : f.fromUsername;
                          return (name || '').toLowerCase().includes(eventInviteeSearch.toLowerCase()) ||
                                 (uname || '').toLowerCase().includes(eventInviteeSearch.toLowerCase());
                        });
                        return filtered.length > 0 ? (
                          <div style={{ background: 'var(--input-bg)', borderRadius: 10, overflow: 'hidden', marginBottom: '0.3rem' }}>
                            {filtered.slice(0, 4).map((f: any) => {
                              const uid = f.fromUid === currentUser.uid ? f.toUid : f.fromUid;
                              const name = f.fromUid === currentUser.uid ? f.toDisplayName : f.fromDisplayName;
                              const already = eventInvitees.includes(uid);
                              return (
                                <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem', cursor: 'pointer', opacity: already ? 0.5 : 1 }}
                                  onClick={() => {
                                    if (!already) { setEventInvitees(prev => [...prev, uid]); setEventInviteeSearch(''); }
                                  }}>
                                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: '#fff', fontWeight: 700 }}>
                                    {(name || '?')[0].toUpperCase()}
                                  </div>
                                  <span style={{ fontSize: '0.83rem', color: 'var(--text-main)' }}>{name}</span>
                                  {already && <span style={{ fontSize: '0.72rem', color: 'var(--primary)', marginLeft: 'auto' }}>✓</span>}
                                </div>
                              );
                            })}
                          </div>
                        ) : null;
                      })()}
                      {/* Selected invitees chips */}
                      {eventInvitees.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                          {eventInvitees.map(uid => {
                            const f = activeFriends.find((fr: any) => fr.fromUid === currentUser.uid ? fr.toUid === uid : fr.fromUid === uid);
                            const name = f ? (f.fromUid === currentUser.uid ? f.toDisplayName : f.fromDisplayName) : uid;
                            return (
                              <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'rgba(124,58,237,0.15)', borderRadius: 20, padding: '0.2rem 0.6rem', fontSize: '0.78rem', color: 'var(--primary)' }}>
                                {name}
                                <button onClick={() => setEventInvitees(prev => prev.filter(u => u !== uid))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', padding: 0, lineHeight: 1 }}>×</button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="agenda-save-btn" onClick={handleSaveEvent} disabled={eventSaving || !eventTitle.trim() || !eventDate || !eventTime}>
                        {eventSaving ? '...' : '✓ Guardar'}
                      </button>
                      <button className="agenda-cancel-btn" onClick={() => { setShowNewEvent(false); setEventTitle(''); setEventDate(''); setEventTime(''); setEventNote(''); setEventInvitees([]); setEventReminderBefore(30); }}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {agendaEvents.length === 0 && !showNewEvent && (
                  <div className="sidebar__empty"><p>No tienes eventos.</p><p>¡Crea uno! 📅</p></div>
                )}

                <div className="agenda-list">
                  {agendaEvents.map(ev => (
                    <div key={ev.id} className={`agenda-event ${ev.done ? 'agenda-event--done' : ''} ${isToday(ev.dateTime) && !ev.done ? 'agenda-event--today' : ''} ${isPast(ev.dateTime) && !ev.done ? 'agenda-event--past' : ''}`}>
                      <button className="agenda-event__check" onClick={() => handleToggleDone(ev.id, ev.done)}>
                        {ev.done ? <CheckCircle2 size={18} color="var(--primary)" /> : <div className="agenda-event__circle" />}
                      </button>
                      <div className="agenda-event__body">
                        <div className="agenda-event__title">{ev.title}</div>
                        <div className="agenda-event__date">
                          <Bell size={11} style={{ marginRight: 3 }} />
                          {formatEventDate(ev.dateTime)}
                          {isToday(ev.dateTime) && !ev.done && <span className="agenda-badge agenda-badge--today">Hoy</span>}
                          {isPast(ev.dateTime) && !ev.done && <span className="agenda-badge agenda-badge--past">Pasado</span>}
                        </div>
                        {ev.note && <div className="agenda-event__note">{ev.note}</div>}
                        {ev.invitees?.length > 0 && (
                          <div style={{ fontSize: '0.73rem', color: 'var(--primary)', marginTop: '0.2rem' }}>
                            👥 {ev.invitees.length} invitado{ev.invitees.length > 1 ? 's' : ''}
                          </div>
                        )}
                        {ev.reminderBefore && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '0.1rem' }}>
                            🔔 {ev.reminderBefore >= 60 ? `${ev.reminderBefore / 60}h` : `${ev.reminderBefore}min`} antes
                          </div>
                        )}
                      </div>
                      <button className="agenda-event__del" onClick={() => handleDeleteEvent(ev.id)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

      </aside>

      {/* Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => { setShowSettings(false); setSettingsPage(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360, padding: 0, overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1.25rem 1.25rem 1rem', borderBottom: '1px solid var(--glass-border)' }}>
              {settingsPage && (
                <button className="sidebar__icon-btn" onClick={() => setSettingsPage(null)} style={{ marginRight: '0.25rem' }}>←</button>
              )}
              <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-main)' }}>
                {settingsPage === 'theme' ? '🎨 Temas' :
                 settingsPage === 'privacy' ? '🔒 Privacidad' :
                 settingsPage === 'support' ? '🎧 Soporte' :
                 settingsPage === 'about' ? 'ℹ️ Acerca de' :
                 settingsPage === 'premium' ? '👑 Premium' :
                 settingsPage === 'delete' ? '⚠️ Eliminar cuenta' :
                 settingsPage === 'language' ? `🌐 ${t.chat.language}` :
                 '⚙️ Configuraciones'}
              </span>
              <button className="sidebar__icon-btn" style={{ marginLeft: 'auto' }} onClick={() => { setShowSettings(false); setSettingsPage(null); }}><X size={18} /></button>
            </div>

            {/* Main menu */}
            {settingsPage === 'language' && (
              <div style={{ padding: '0.5rem' }}>
                {(['ES', 'EN'] as const).map(l => (
                  <div key={l} onClick={() => setLang(l)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: 12, cursor: 'pointer', background: lang === l ? 'var(--primary)' : 'none', color: lang === l ? '#fff' : 'var(--text-main)', marginBottom: '0.25rem', transition: 'all 0.15s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ fontSize: '1.3rem' }}>{l === 'ES' ? '🇪🇸' : '🇺🇸'}</span>
                      <span style={{ fontSize: '0.9rem', fontWeight: lang === l ? 700 : 400 }}>{l === 'ES' ? 'Español' : 'English'}</span>
                    </div>
                    {lang === l && <span>✓</span>}
                  </div>
                ))}
              </div>
            )}

            {!settingsPage && (
              <div style={{ padding: '0.5rem' }}>

                {/* Premium banner */}
                <div style={{ margin: '0.5rem', borderRadius: 14, background: 'linear-gradient(135deg, #7c3aed, #a855f7)', padding: '1rem', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}
                  onClick={() => setSettingsPage('premium')}>
                  <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <Crown size={20} color="#fbbf24" />
                    <div>
                      <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.92rem' }}>Hazte Premium</div>
                      <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)' }}>Temas, stickers y más</div>
                    </div>
                    <ChevronRight size={16} color="rgba(255,255,255,0.7)" style={{ marginLeft: 'auto' }} />
                  </div>
                </div>

                {/* Theme toggle */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: 12, cursor: 'pointer' }}
                  onClick={toggleTheme}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-main)' }}>
                    {isDark ? <Moon size={18} color="var(--text-dim)" /> : <Sun size={18} color="var(--text-dim)" />}
                    <span style={{ fontSize: '0.9rem' }}>{isDark ? 'Modo oscuro' : 'Modo claro'}</span>
                  </div>
                  <div style={{ width: 40, height: 22, borderRadius: 99, background: isDark ? 'var(--primary)' : 'var(--glass-border)', position: 'relative', transition: 'background 0.2s' }}>
                    <div style={{ position: 'absolute', top: 3, left: isDark ? 20 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                  </div>
                </div>

                {[
                  { id: 'theme', icon: <Palette size={18} color="var(--text-dim)" />, label: t.chat.theme },
                  { id: 'privacy', icon: <Shield size={18} color="var(--text-dim)" />, label: t.chat.privacy },
                  { id: 'support', icon: <HeadphonesIcon size={18} color="var(--text-dim)" />, label: t.chat.support },
                  { id: 'about', icon: <Info size={18} color="var(--text-dim)" />, label: t.chat.about },
                  { id: 'language', icon: <span style={{ fontSize: '1rem' }}>🌐</span>, label: t.chat.language },
                ].map(item => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: 12, cursor: 'pointer', transition: 'background 0.15s' }}
                    onClick={() => setSettingsPage(item.id)}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--input-bg)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-main)' }}>
                      {item.icon}<span style={{ fontSize: '0.9rem' }}>{item.label}</span>
                    </div>
                    <ChevronRight size={16} color="var(--text-dim)" />
                  </div>
                ))}

                <div style={{ borderTop: '1px solid var(--glass-border)', margin: '0.5rem 0' }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', borderRadius: 12, cursor: 'pointer', color: 'var(--text-main)', transition: 'background 0.15s' }}
                  onClick={handleLogout}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--input-bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                  <LogOut size={18} color="var(--text-dim)" />
                  <span style={{ fontSize: '0.9rem' }}>Cerrar sesión</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', borderRadius: 12, cursor: 'pointer', color: '#ef4444', transition: 'background 0.15s' }}
                  onClick={() => setSettingsPage('delete')}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.08)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                  <Trash2 size={18} color="#ef4444" />
                  <span style={{ fontSize: '0.9rem' }}>Eliminar cuenta</span>
                </div>
              </div>
            )}

            {/* Theme page */}
            {settingsPage === 'theme' && (
              <div style={{ padding: '1rem' }}>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginBottom: '1rem' }}>Elige el estilo visual del chat</p>
                {[
                  { id: 'default', color: '#7c3aed', label: 'Morado',      desc: 'El tema original de TolkX' },
                  { id: 'ocean',   color: '#0ea5e9', label: 'Océano',      desc: 'Azules profundos' },
                  { id: 'nature',  color: '#22c55e', label: 'Naturaleza',  desc: 'Tonos verdes y tierra' },
                  { id: 'rose',    color: '#ec4899', label: 'Rosa',        desc: 'Tonos rosados suaves' },
                  { id: 'sunset',  color: '#f97316', label: 'Atardecer',   desc: 'Naranja cálido' },
                  { id: 'pixel',   color: '#7c3aed', label: '👾 Pixel',    desc: '8-bit retro morado' },
                ].map(th => (
                  <div key={th.id} onClick={() => handleChatTheme(th.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', borderRadius: 12, cursor: 'pointer', marginBottom: '0.35rem', border: chatTheme === th.id ? `1px solid ${th.color}` : '1px solid transparent', background: chatTheme === th.id ? 'var(--input-bg)' : 'none', transition: 'all 0.15s' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: th.color, flexShrink: 0, boxShadow: chatTheme === th.id ? `0 0 0 3px ${th.color}40` : 'none', transition: 'box-shadow 0.15s' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-main)' }}>{th.label}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{th.desc}</div>
                    </div>
                    {chatTheme === th.id && <span style={{ color: th.color, fontSize: '1rem' }}>✓</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Privacy page */}
            {settingsPage === 'privacy' && (
              <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

                {/* Toggle: Estado en línea */}
                <div style={{ padding: '0.85rem', borderRadius: 12, background: 'var(--input-bg)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-main)' }}>Estado en línea</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.2rem' }}>
                      {hideOnlineStatus ? 'Nadie ve cuando estás conectado' : 'Tus contactos ven cuando estás activo'}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      const next = !hideOnlineStatus;
                      setHideOnlineStatus(next);
                      await setDoc(doc(db, 'users', currentUser.uid), { hideOnlineStatus: next }, { merge: true });
                    }}
                    style={{
                      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', flexShrink: 0,
                      background: hideOnlineStatus ? 'var(--text-dim)' : 'var(--primary)',
                      position: 'relative', transition: 'background 0.2s',
                    }}>
                    <div style={{
                      position: 'absolute', top: 3, left: hideOnlineStatus ? 3 : 23,
                      width: 18, height: 18, borderRadius: '50%', background: '#fff',
                      transition: 'left 0.2s',
                    }} />
                  </button>
                </div>

                {/* Toggle: Última conexión */}
                <div style={{ padding: '0.85rem', borderRadius: 12, background: 'var(--input-bg)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-main)' }}>Última conexión</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.2rem' }}>
                      {hideLastSeen ? 'Nadie ve tu última conexión' : 'Tus contactos ven cuándo estuviste activo'}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      const next = !hideLastSeen;
                      setHideLastSeen(next);
                      await setDoc(doc(db, 'users', currentUser.uid), { hideLastSeen: next }, { merge: true });
                    }}
                    style={{
                      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', flexShrink: 0,
                      background: hideLastSeen ? 'var(--text-dim)' : 'var(--primary)',
                      position: 'relative', transition: 'background 0.2s',
                    }}>
                    <div style={{
                      position: 'absolute', top: 3, left: hideLastSeen ? 3 : 23,
                      width: 18, height: 18, borderRadius: '50%', background: '#fff',
                      transition: 'left 0.2s',
                    }} />
                  </button>
                </div>

                {/* Estado personalizado */}
                <div style={{ borderTop: '1px solid var(--glass-border)', marginTop: '0.25rem', padding: '0.35rem 0.25rem 0' }}>
                  {!showCustomInput ? (
                    <button className="status-menu__item" onClick={() => { setShowCustomInput(true); setCustomInput(customStatusText); }}>
                      ✏️ Estado personalizado
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.35rem', padding: '0.25rem' }}>
                      <input
                        autoFocus
                        value={customInput}
                        onChange={e => setCustomInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveCustomStatus(); if (e.key === 'Escape') setShowCustomInput(false); }}
                        placeholder="Ej: En reunión..."
                        maxLength={40}
                        style={{ flex: 1, background: 'var(--input-bg)', border: '1px solid var(--glass-border)', borderRadius: 8, padding: '0.3rem 0.5rem', color: 'var(--text-main)', fontSize: '0.8rem', outline: 'none', fontFamily: 'DM Sans, sans-serif' }}
                      />
                      <button onClick={handleSaveCustomStatus}
                        style={{ background: 'var(--primary)', border: 'none', borderRadius: 8, padding: '0 0.5rem', color: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}>✓</button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Support page */}
            {settingsPage === 'support' && (
              <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <a href="mailto:soporte@tolkx.app" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem', borderRadius: 12, background: 'var(--input-bg)', textDecoration: 'none', color: 'var(--text-main)' }}>
                  <span style={{ fontSize: '1.2rem' }}>✉️</span>
                  <div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>Email</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>soporte@tolkx.app</div>
                  </div>
                </a>
                <a href="https://tolkx.app/help" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem', borderRadius: 12, background: 'var(--input-bg)', textDecoration: 'none', color: 'var(--text-main)' }}>
                  <span style={{ fontSize: '1.2rem' }}>📖</span>
                  <div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>Centro de ayuda</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Guías y preguntas frecuentes</div>
                  </div>
                </a>
              </div>
            )}

            {/* About page */}
            {settingsPage === 'about' && (
              <div style={{ padding: '1.5rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 900, color: 'var(--primary)', fontFamily: 'DM Sans, sans-serif' }}>TolkX</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>Versión 1.0.0 Beta</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', lineHeight: 1.6, maxWidth: 240 }}>Mensajes seguros, rápidos y sin límites. Hecho con ♥</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.5rem' }}>© {new Date().getFullYear()} TolkX · Todos los derechos reservados</div>
              </div>
            )}

            {/* Premium page */}
            {settingsPage === 'premium' && (
              <div style={{ padding: '1rem' }}>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '2rem' }}>👑</div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-main)' }}>TolkX Premium</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Próximamente</div>
                </div>
                {[
                  '🎨 Temas exclusivos y retro pixel',
                  '😄 Stickers y emojis únicos',
                  '📁 Archivos hasta 2GB',
                  '⭐ Badge Premium en tu perfil',
                  '🚀 Prioridad en soporte',
                  '📵 Sin anuncios nunca',
                ].map(f => (
                  <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0', fontSize: '0.88rem', color: 'var(--text-main)', borderBottom: '1px solid var(--glass-border)' }}>{f}</div>
                ))}
                <button style={{ width: '100%', marginTop: '1.25rem', padding: '0.75rem', borderRadius: 12, background: 'linear-gradient(135deg, #7c3aed, #a855f7)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                  Notificarme cuando esté disponible
                </button>
              </div>
            )}

            {/* Delete account page */}
            {settingsPage === 'delete' && (
              <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ padding: '0.85rem', borderRadius: 12, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', fontSize: '0.82rem', color: '#ef4444', lineHeight: 1.6 }}>
                  ⚠️ Esta acción es permanente. Todos tus mensajes, chats y datos serán eliminados y no se podrán recuperar.
                </div>
                <button onClick={handleDeleteAccount} disabled={deletingAccount}
                  style={{ padding: '0.75rem', borderRadius: 12, background: '#ef4444', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                  {deletingAccount ? 'Eliminando...' : '🗑️ Eliminar mi cuenta permanentemente'}
                </button>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfile && (
        <div className="modal-overlay" onClick={() => setShowProfile(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <p className="modal__title" style={{ margin: 0 }}>✏️ Editar perfil</p>
              <button className="sidebar__icon-btn" onClick={() => setShowProfile(false)}><X size={18} /></button>
            </div>

            {/* Photo */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.25rem', gap: '0.75rem' }}>
              <div style={{ position: 'relative', width: 80, height: 80 }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--primary)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
                  {(userProfile?.photoURL || currentUser.photoURL)
                    ? <img src={userProfile?.photoURL || currentUser.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : getInitials(userProfile?.displayName || currentUser.displayName || 'U')}
                </div>
                <label style={{ position: 'absolute', bottom: 0, right: 0, width: 26, height: 26, borderRadius: '50%', background: 'var(--primary)', border: '2px solid var(--card-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.7rem' }}>
                  📷
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) handlePhotoUpload(e.target.files[0]); }} />
                </label>
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>@{userProfile?.username || ''} · username permanente</div>
            </div>

            {/* Name */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.78rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.35rem' }}>Nombre para mostrar</label>
              <input
                className="modal__input"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                placeholder="Tu nombre..."
                maxLength={40}
                style={{ marginBottom: 0 }}
              />
            </div>

            {/* Bio */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.78rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.35rem' }}>Bio</label>
              <textarea
                className="modal__input"
                value={editBio}
                onChange={e => setEditBio(e.target.value)}
                placeholder="Cuéntanos algo de ti..."
                maxLength={120}
                rows={3}
                style={{ resize: 'none', marginBottom: 0 }}
              />
            </div>

            {profileError && <p className="modal__error">{profileError}</p>}

            <button className="modal__btn modal__btn--primary" style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => handleSaveProfile()} disabled={profileSaving}>
              {profileSaving ? 'Guardando...' : '✓ Guardar cambios'}
            </button>
            <button style={{ width: '100%', marginTop: '0.5rem', padding: '0.6rem', borderRadius: 12, border: '1px solid var(--glass-border)', background: 'none', color: 'var(--text-main)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
              onClick={() => { setShowProfile(false); setShowQR(true); }}>
              <QrCode size={16} /> Mi QR y enlace
            </button>
          </div>
        </div>
      )}

      {/* QR Modal */}
      {showQR && (() => {
        const username = userProfile?.username || currentUser.uid;
        const profileUrl = `${window.location.origin}/u/${username}`;
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(profileUrl)}&color=7c3aed&bgcolor=ffffff`;
        const handleCopy = () => {
          navigator.clipboard.writeText(profileUrl);
          const btn = document.getElementById('qr-copy-btn');
          if (btn) { btn.textContent = '✓ Copiado'; setTimeout(() => { btn.textContent = '⎘ Copiar enlace'; }, 2000); }
        };
        return (
          <div className="modal-overlay" onClick={() => setShowQR(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 320, textAlign: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-main)' }}>🔗 Mi perfil</span>
                <button className="sidebar__icon-btn" onClick={() => setShowQR(false)}><X size={18} /></button>
              </div>
              {/* QR Code */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                <div style={{ padding: '0.75rem', background: '#fff', borderRadius: 16, display: 'inline-block' }}>
                  <img src={qrUrl} alt="QR" width={180} height={180} style={{ display: 'block', borderRadius: 8 }} />
                </div>
              </div>
              {/* Username */}
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--text-main)', marginBottom: '0.25rem' }}>
                @{username}
              </div>
              {/* Link */}
              <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginBottom: '1rem', wordBreak: 'break-all' }}>
                {profileUrl}
              </div>
              {/* Buttons */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button id="qr-copy-btn" onClick={handleCopy} style={{ flex: 1, padding: '0.6rem', borderRadius: 12, border: '1px solid var(--glass-border)', background: 'none', color: 'var(--text-main)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', transition: 'all 0.2s' }}>
                  ⎘ Copiar enlace
                </button>
                <button onClick={() => { const a = document.createElement('a'); a.href = qrUrl; a.download = `tolkx-${username}.png`; a.click(); }} style={{ flex: 1, padding: '0.6rem', borderRadius: 12, border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                  <QrCode size={14} /> Descargar QR
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add Friend Modal */}
      {showAddFriend && (
        <div className="modal-overlay" onClick={() => setShowAddFriend(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <p className="modal__title" style={{ margin: 0 }}>Agregar amigo</p>
              <button className="sidebar__icon-btn" onClick={() => setShowAddFriend(false)}><X size={18} /></button>
            </div>
            <div className="sidebar__search-wrap" style={{ marginBottom: '0.75rem' }}>
              <Search size={15} color="var(--text-dim)" />
              <input className="sidebar__search-input" placeholder="@usuario o correo@email.com" value={friendInput}
                onChange={e => handleSearchInput(e.target.value)} autoFocus />
              {searching && <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>...</span>}
            </div>
            {friendError && <p className="modal__error">{friendError}</p>}
            {friendSuccess && <p className="modal__success">{friendSuccess}</p>}
            {searchResult && (
              <div className="friend-search-result">
                <div className="sidebar__item-avatar" style={{ width: 40, height: 40, fontSize: '0.9rem' }}>
                  {searchResult.photoURL ? <img src={searchResult.photoURL} alt="" /> : getInitials(searchResult.displayName || searchResult.username || 'U')}
                </div>
                <div className="friend-item__info">
                  <div className="friend-item__name">{searchResult.displayName || searchResult.username}</div>
                  <div className="friend-item__username">@{searchResult.username}</div>
                </div>
                <button className="modal__btn modal__btn--primary" style={{ padding: '0.4rem 0.9rem', fontSize: '0.82rem', flex: 'none' }}
                  onClick={handleSendRequest} disabled={addLoading}>
                  {addLoading ? '...' : 'Agregar'}
                </button>
              </div>
            )}
            {friendInput.length >= 2 && !searching && !searchResult && !friendError && !friendSuccess && (
              <p style={{ color: 'var(--text-dim)', fontSize: '0.83rem', textAlign: 'center', margin: '0.5rem 0' }}>No se encontró ningún usuario.</p>
            )}
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateGroup && (
        <div className="modal-overlay" onClick={() => setShowCreateGroup(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <p className="modal__title" style={{ margin: 0 }}>👥 Nuevo grupo</p>
              <button className="sidebar__icon-btn" onClick={() => setShowCreateGroup(false)}><X size={18} /></button>
            </div>

            {/* Group photo */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <label style={{ position: 'relative', width: 70, height: 70, borderRadius: '50%', background: 'var(--input-bg)', border: '2px dashed var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', fontSize: '1.5rem' }}>
                {groupPhoto ? <img src={groupPhoto} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" /> : '📷'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                  if (e.target.files?.[0]) {
                    const reader = new FileReader();
                    reader.onload = (ev) => setGroupPhoto(ev.target?.result as string);
                    reader.readAsDataURL(e.target.files[0]);
                  }
                }} />
              </label>
            </div>

            {/* Group name */}
            <input
              className="sidebar__search-input"
              style={{ width: '100%', marginBottom: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '10px', background: 'var(--input-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-main)', fontFamily: 'DM Sans, sans-serif' }}
              placeholder="Nombre del grupo..."
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              autoFocus
            />
            <textarea
              style={{ width: '100%', marginBottom: '1rem', padding: '0.5rem 0.75rem', borderRadius: '10px', background: 'var(--input-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-main)', fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', resize: 'none', outline: 'none', minHeight: 56 }}
              placeholder="Descripción del grupo (opcional)..."
              value={groupDescription}
              onChange={e => setGroupDescription(e.target.value)}
              maxLength={120}
            />

            {/* Select members */}
            <div className="friends-section-label" style={{ marginBottom: '0.5rem' }}>
              Seleccionar miembros ({selectedMembers.length} seleccionados)
            </div>

            {activeFriends.length === 0 && (
              <p style={{ color: 'var(--text-dim)', fontSize: '0.83rem', textAlign: 'center' }}>
                No tienes amigos para agregar al grupo.
              </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
              {activeFriends.map((friend: any) => {
                const otherUid = friend.fromUid === currentUser.uid ? friend.toUid : friend.fromUid;
                const name = friend.fromUid === currentUser.uid
                  ? (friend.toDisplayName || friend.toUsername)
                  : (friend.fromDisplayName || friend.fromUsername);
                const username = friend.fromUid === currentUser.uid ? friend.toUsername : friend.fromUsername;
                const isSelected = selectedMembers.includes(otherUid);

                return (
                  <div
                    key={friend.id}
                    className={`group-member-item ${isSelected ? 'group-member-item--selected' : ''}`}
                    onClick={() => toggleMember(otherUid)}
                  >
                    <div className="sidebar__item-avatar" style={{ width: 36, height: 36, fontSize: '0.8rem' }}>
                      {getInitials(name)}
                    </div>
                    <div className="friend-item__info">
                      <div className="friend-item__name">{name}</div>
                      <div className="friend-item__username">@{username}</div>
                    </div>
                    <div className={`group-member-check ${isSelected ? 'group-member-check--active' : ''}`}>
                      {isSelected ? '✓' : ''}
                    </div>
                  </div>
                );
              })}
            </div>

            {groupError && <p className="modal__error">{groupError}</p>}

            <button
              className="modal__btn modal__btn--primary"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={handleCreateGroup}
              disabled={groupLoading}
            >
              {groupLoading ? 'Creando...' : '✓ Crear grupo'}
            </button>
          </div>
        </div>
      )}
      {/* Daily Summary Popup */}
      {showDailySummary && (() => {
        const now = new Date();
        const todayEvents = agendaEvents.filter(ev => {
          if (ev.done) return false;
          const d = ev.dateTime?.toDate ? ev.dateTime.toDate() : new Date(ev.dateTime);
          return d.toDateString() === now.toDateString();
        }).sort((a, b) => {
          const da = a.dateTime?.toDate ? a.dateTime.toDate() : new Date(a.dateTime);
          const db2 = b.dateTime?.toDate ? b.dateTime.toDate() : new Date(b.dateTime);
          return da - db2;
        });
        const upcomingEvents = agendaEvents.filter(ev => {
          if (ev.done) return false;
          const d = ev.dateTime?.toDate ? ev.dateTime.toDate() : new Date(ev.dateTime);
          return d > now && d.toDateString() !== now.toDateString();
        }).slice(0, 3);
        const recentNotes = notes.slice(0, 3);
        const hour = now.getHours();
        const greeting = hour < 12 ? '🌅 Buenos días' : hour < 18 ? '☀️ Buenas tardes' : '🌙 Buenas noches';
        const name = userProfile?.displayName || currentUser.displayName || '';

        return (
          <div className="modal-overlay" onClick={() => setShowDailySummary(false)}>
            <div className="daily-summary" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="daily-summary__header">
                <div>
                  <div className="daily-summary__greeting">{greeting}, {name.split(' ')[0]} 👋</div>
                  <div className="daily-summary__date">
                    {now.toLocaleDateString('es-DO', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </div>
                </div>
                <button className="sidebar__icon-btn" onClick={() => setShowDailySummary(false)}>
                  <X size={18} />
                </button>
              </div>

              <div className="daily-summary__body">

                {/* Today's events */}
                <div className="daily-summary__section">
                  <div className="daily-summary__section-title">
                    <CalendarDays size={14} /> Hoy
                    {todayEvents.length > 0 && <span className="daily-summary__count">{todayEvents.length}</span>}
                  </div>
                  {todayEvents.length === 0 ? (
                    <div className="daily-summary__empty">Sin eventos para hoy ✨</div>
                  ) : (
                    todayEvents.map(ev => {
                      const d = ev.dateTime?.toDate ? ev.dateTime.toDate() : new Date(ev.dateTime);
                      return (
                        <div key={ev.id} className="daily-summary__item daily-summary__item--event">
                          <div className="daily-summary__item-dot" />
                          <div className="daily-summary__item-body">
                            <div className="daily-summary__item-title">{ev.title}</div>
                            <div className="daily-summary__item-sub">
                              {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              {ev.note && ` · ${ev.note}`}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Upcoming events */}
                {upcomingEvents.length > 0 && (
                  <div className="daily-summary__section">
                    <div className="daily-summary__section-title">
                      <Bell size={14} /> Próximos
                    </div>
                    {upcomingEvents.map(ev => {
                      const d = ev.dateTime?.toDate ? ev.dateTime.toDate() : new Date(ev.dateTime);
                      return (
                        <div key={ev.id} className="daily-summary__item">
                          <div className="daily-summary__item-dot daily-summary__item-dot--dim" />
                          <div className="daily-summary__item-body">
                            <div className="daily-summary__item-title">{ev.title}</div>
                            <div className="daily-summary__item-sub">
                              {d.toLocaleDateString('es-DO', { weekday: 'short', month: 'short', day: 'numeric' })} · {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Recent notes */}
                {recentNotes.length > 0 && (
                  <div className="daily-summary__section">
                    <div className="daily-summary__section-title">
                      📝 Notas recientes
                    </div>
                    {recentNotes.map(note => (
                      <div key={note.id} className="daily-summary__item daily-summary__item--note"
                        onClick={() => { setShowDailySummary(false); setTab('agenda'); setAgendaSubTab('notes'); }}>
                        <div className="daily-summary__item-body">
                          <div className="daily-summary__item-title">{note.title}</div>
                          {note.body && <div className="daily-summary__item-sub">{note.body.slice(0, 60)}{note.body.length > 60 ? '...' : ''}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              </div>

              {/* Footer */}
              <div className="daily-summary__footer">
                <button className="daily-summary__btn-agenda" onClick={() => { setShowDailySummary(false); setTab('agenda'); }}>
                  <CalendarDays size={14} /> Ver agenda
                </button>
                <button className="daily-summary__btn-close" onClick={() => setShowDailySummary(false)}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {chatCtxMenu && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200 }} onClick={() => setChatCtxMenu(null)}>
          <div className="chat-ctx-menu" style={{ top: chatCtxMenu.y, left: chatCtxMenu.x }} onClick={e => e.stopPropagation()}>
            <div className="chat-ctx-menu__header">
              <div className="chat-ctx-menu__avatar">
                {chatCtxMenu.chat.isGroup ? (
                  <span style={{ fontSize: '1.5rem' }}>👥</span>
                ) : chatCtxMenu.chat.photoURL ? (
                  <img src={chatCtxMenu.chat.photoURL} alt="" />
                ) : (
                  getInitials(chatCtxMenu.chat.displayName)
                )}
                {!chatCtxMenu.chat.isGroup && chatCtxMenu.chat.online && <span className="online-dot" style={{ width: 10, height: 10, bottom: 1, right: 1 }} />}
              </div>
              <div>
                <div className="chat-ctx-menu__name">{chatCtxMenu.chat.displayName}</div>
                <div className="chat-ctx-menu__status">{!chatCtxMenu.chat.isGroup && (chatCtxMenu.chat.online ? '🟢 En línea' : '⚫ Desconectado')}</div>
              </div>
            </div>
            <div className="chat-ctx-menu__divider" />
            <button className="chat-ctx-menu__item" onClick={() => { onSelectChat(chatCtxMenu.chat); setChatCtxMenu(null); }}>
              💬 Enviar mensaje
            </button>
            <div className="chat-ctx-menu__divider" />
            <button className="chat-ctx-menu__item" onClick={async () => {
              const isPinned = chatCtxMenu.chat.pinnedBy;
              await updateDoc(doc(db, 'chats', chatCtxMenu.chat.chatId), {
                [`pinnedBy.${currentUser.uid}`]: isPinned ? null : true
              });
              setChatCtxMenu(null);
            }}>
              {chatCtxMenu.chat.pinnedBy ? <><PinOff size={15} /> Desfijar chat</> : <><Pin size={15} /> Fijar chat</>}
            </button>
            <button className="chat-ctx-menu__item" onClick={async () => {
              const isArchived = chatCtxMenu.chat.archivedBy;
              await updateDoc(doc(db, 'chats', chatCtxMenu.chat.chatId), {
                [`archivedBy.${currentUser.uid}`]: isArchived ? null : true
              });
              setChatCtxMenu(null);
            }}>
              {chatCtxMenu.chat.archivedBy ? <><ArchiveRestore size={15} /> Desarchivar</> : <><Archive size={15} /> Archivar</>}
            </button>
            <div className="chat-ctx-menu__divider" />
            <button className="chat-ctx-menu__item chat-ctx-menu__item--danger" onClick={async () => {
              await updateDoc(doc(db, 'chats', chatCtxMenu.chat.chatId), {
                [`deletedBy.${currentUser.uid}`]: true,
                [`deletedAt.${currentUser.uid}`]: new Date().getTime(),
              });
              setChatCtxMenu(null);
            }}>
              <Trash2 size={15} /> Eliminar chat
            </button>
          </div>
        </div>
      )}
    </>
  );
};